import React, { useState, useMemo } from 'react';
import { Calculator, ArrowRight, Info, Phone, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import Layout from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../data/mock';

const CalculatorPage = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  const [config, setConfig] = useState({
    type: 'arc', // arc, drop, house
    width: 3,
    length: 4,
    frame: '20x20', // 20x20, 25x25, 40x20
    polycarbonate: '0.52', // 0.52, 0.6, 0.7
    options: {
      fortochka: false,
      termoprivod: false,
      fundament: false,
      montazh: false
    }
  });

  const [showPolyInfo, setShowPolyInfo] = useState(false);

  // Базовые цены за каркас (для 3x4)
  const basePrices = {
    arc: {
      '20x20': 18000,
      '25x25': 21000,
      '40x20': 26000
    },
    drop: {
      '20x20': 19000
    },
    house: {
      '20x20': 24000,
      '25x25': 27000,
      '40x20': 32000
    }
  };

  // Множители за длину
  const lengthMultipliers = {
    4: 1,
    6: 1.35,
    8: 1.65,
    10: 2.0
  };

  // Множитель за ширину для домика
  const widthMultipliers = {
    2.5: 1,
    3: 1,
    4: 1.25 // только для домика
  };

  // Цены поликарбоната (доплата к базе)
  const polycarbonatePrice = {
    '0.52': 0,
    '0.6': 2000,
    '0.7': 4000
  };

  // Цены опций
  const optionPrices = {
    fortochka: 1500,
    termoprivod: 2500,
    fundament: {
      4: 3500,
      6: 4000,
      8: 5000,
      10: 6500
    },
    montazh: {
      arc: { 4: 3500, 6: 4500, 8: 5000, 10: 6000 },
      drop: { 4: 3500, 6: 4500, 8: 5000, 10: 6000 },
      house: { 4: 4500, 6: 6000, 8: 8000, 10: 10500 }
    }
  };

  const typeNames = {
    arc: 'Арочная',
    drop: 'Капелька',
    house: 'Домиком (прямостенная)'
  };

  const frameNames = {
    '20x20': 'Труба 20x20 мм',
    '25x25': 'Труба 25x25 мм',
    '40x20': 'Труба 40x20 мм'
  };

  const polyNames = {
    '0.52': '4 мм, плотность 0.52 кг/м²',
    '0.6': '4 мм, плотность 0.6 кг/м²',
    '0.7': '4 мм, плотность 0.7 кг/м²'
  };

  // Доступные каркасы для типа теплицы
  const availableFrames = useMemo(() => {
    if (config.type === 'drop') {
      return ['20x20'];
    }
    return ['20x20', '25x25', '40x20'];
  }, [config.type]);

  // Доступные ширины
  const availableWidths = useMemo(() => {
    if (config.type === 'house') {
      return [3, 4];
    }
    return [2.5, 3];
  }, [config.type]);

  // При смене типа - сбросить каркас если недоступен
  const handleTypeChange = (type) => {
    let newFrame = config.frame;
    let newWidth = config.width;
    
    if (type === 'drop' && config.frame !== '20x20') {
      newFrame = '20x20';
    }
    
    if (type === 'house') {
      if (config.width === 2.5) newWidth = 3;
    } else {
      if (config.width === 4) newWidth = 3;
    }
    
    setConfig(prev => ({ ...prev, type, frame: newFrame, width: newWidth }));
  };

  const calculatePrice = () => {
    // Базовая цена
    const framePrice = basePrices[config.type]?.[config.frame] || basePrices[config.type]['20x20'];
    let total = framePrice;
    
    // Множитель длины
    total *= lengthMultipliers[config.length];
    
    // Множитель ширины (только для домика при ширине 4м)
    if (config.type === 'house' && config.width === 4) {
      total *= widthMultipliers[4];
    }
    
    // Доплата за поликарбонат
    total += polycarbonatePrice[config.polycarbonate];
    
    // Опции
    if (config.options.fortochka) {
      total += optionPrices.fortochka;
    }
    if (config.options.termoprivod) {
      total += optionPrices.termoprivod;
    }
    if (config.options.fundament) {
      total += optionPrices.fundament[config.length];
    }
    if (config.options.montazh) {
      total += optionPrices.montazh[config.type][config.length];
    }
    
    return Math.round(total);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  const handleOptionToggle = (option) => {
    setConfig(prev => ({
      ...prev,
      options: {
        ...prev.options,
        [option]: !prev.options[option]
      }
    }));
  };

  const handleAddToCart = () => {
    // Собираем детали конфигурации
    const configDetails = {
      type: typeNames[config.type],
      frame: frameNames[config.frame],
      polycarbonate: polyNames[config.polycarbonate],
      polycarbonateThickness: config.polycarbonate === '0.52' ? '4мм' : config.polycarbonate === '0.6' ? '6мм' : '8мм',
      options: {
        fortochka: config.options.fortochka ? 'Да' : 'Нет',
        termoprivod: config.options.termoprivod ? 'Да' : 'Нет',
        fundament: config.options.fundament ? 'Да' : 'Нет',
        montazh: config.options.montazh ? 'Да' : 'Нет'
      }
    };

    const customProduct = {
      id: Date.now(),
      name: `Теплица ${typeNames[config.type]} ${config.width}x${config.length} м`,
      slug: `custom-${Date.now()}`,
      price: calculatePrice(),
      size: { width: config.width, length: config.length, height: config.type === 'house' ? 2.5 : 2.1 },
      frame: frameNames[config.frame],
      polycarbonate: polyNames[config.polycarbonate],
      polycarbonateThickness: configDetails.polycarbonateThickness,
      images: ['https://customer-assets.emergentagent.com/job_open-repo-edits/artifacts/sq511w1k_image-01-02-26-03-15.jpeg'],
      inStock: true,
      calculatorConfig: configDetails, // Добавляем детали конфигурации
      isCustom: true // Флаг что это из калькулятора
    };
    addToCart(customProduct);
    navigate('/cart');
  };

  const totalPrice = calculatePrice();

  return (
    <Layout>
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Calculator className="w-8 h-8 text-emerald-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Калькулятор стоимости</h1>
          </div>
          <p className="text-gray-600">Рассчитайте стоимость теплицы по вашим параметрам</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Configuration */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-8">
              
              {/* Type */}
              <div>
                <Label className="text-base font-semibold mb-4 block">Тип теплицы</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Object.entries(typeNames).map(([key, name]) => (
                    <button
                      key={key}
                      onClick={() => handleTypeChange(key)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        config.type === key
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium text-gray-900">{name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Frame (каркас) */}
              <div>
                <Label className="text-base font-semibold mb-4 block">Каркас (сечение трубы)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {availableFrames.map(frame => (
                    <button
                      key={frame}
                      onClick={() => setConfig(prev => ({ ...prev, frame }))}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        config.frame === frame
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium text-gray-900">{frameNames[frame]}</p>
                      {frame !== '20x20' && (
                        <p className="text-xs text-emerald-600 mt-1">Усиленный</p>
                      )}
                    </button>
                  ))}
                </div>
                {config.type === 'drop' && (
                  <p className="text-sm text-gray-500 mt-2">
                    * Для теплицы "Капелька" доступен только каркас 20x20 мм
                  </p>
                )}
              </div>

              {/* Width */}
              <div>
                <Label className="text-base font-semibold mb-4 block">
                  Ширина: <span className="text-emerald-600">{config.width} м</span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {availableWidths.map(w => (
                    <button
                      key={w}
                      onClick={() => setConfig(prev => ({ ...prev, width: w }))}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        config.width === w
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {w} м
                    </button>
                  ))}
                </div>
              </div>

              {/* Length */}
              <div>
                <Label className="text-base font-semibold mb-4 block">
                  Длина: <span className="text-emerald-600">{config.length} м</span>
                </Label>
                <div className="grid grid-cols-4 gap-3">
                  {[4, 6, 8, 10].map(l => (
                    <button
                      key={l}
                      onClick={() => setConfig(prev => ({ ...prev, length: l }))}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        config.length === l
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {l} м
                    </button>
                  ))}
                </div>
              </div>

              {/* Polycarbonate */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Label className="text-base font-semibold">Поликарбонат</Label>
                  <button 
                    onClick={() => setShowPolyInfo(!showPolyInfo)}
                    className="text-emerald-600 hover:text-emerald-700"
                  >
                    <Info className="w-5 h-5" />
                  </button>
                </div>
                
                {showPolyInfo && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Что такое плотность поликарбоната?</h4>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      <strong>Плотность</strong> — это вес квадратного метра листа. Чем выше плотность — тем толще стенки ячеек поликарбоната, а значит он прочнее и дольше служит.
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-blue-800">
                      <li><strong>0.52 кг/м²</strong> — экономичный вариант, срок службы 5-7 лет. Подойдёт для временных теплиц.</li>
                      <li><strong>0.6 кг/м²</strong> — оптимальный выбор, срок службы 8-10 лет. Рекомендуем для большинства случаев.</li>
                      <li><strong>0.7 кг/м²</strong> — премиум качество, срок службы 12-15 лет. Для тех, кто строит на долгие годы.</li>
                    </ul>
                  </div>
                )}
                
                <div className="space-y-3">
                  {Object.entries(polyNames).map(([key, name]) => (
                    <button
                      key={key}
                      onClick={() => setConfig(prev => ({ ...prev, polycarbonate: key }))}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left flex justify-between items-center ${
                        config.polycarbonate === key
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{name}</p>
                        {key === '0.52' && <p className="text-xs text-gray-500">Экономичный вариант</p>}
                        {key === '0.6' && <p className="text-xs text-emerald-600">Рекомендуем</p>}
                        {key === '0.7' && <p className="text-xs text-amber-600">Премиум качество</p>}
                      </div>
                      {polycarbonatePrice[key] > 0 && (
                        <span className="text-gray-600">+{formatPrice(polycarbonatePrice[key])} ₽</span>
                      )}
                    </button>
                  ))}
                </div>
                
                <button 
                  onClick={() => setShowPolyInfo(!showPolyInfo)}
                  className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 underline"
                >
                  {showPolyInfo ? 'Скрыть информацию' : 'В чём отличие плотности? Как влияет на срок службы?'}
                </button>
              </div>

              {/* Options */}
              <div>
                <Label className="text-base font-semibold mb-4 block">Дополнительные опции</Label>
                <div className="space-y-3">
                  
                  {/* Форточка */}
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      config.options.fortochka
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Checkbox
                      checked={config.options.fortochka}
                      onCheckedChange={() => handleOptionToggle('fortochka')}
                    />
                    <span className="flex-1">
                      <span className="font-medium">Дополнительная форточка</span>
                    </span>
                    <span className="text-gray-600">+{formatPrice(optionPrices.fortochka)} ₽</span>
                  </label>
                  
                  {/* Термопривод */}
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      config.options.termoprivod
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Checkbox
                      checked={config.options.termoprivod}
                      onCheckedChange={() => handleOptionToggle('termoprivod')}
                    />
                    <span className="flex-1">
                      <span className="font-medium">Термопривод</span>
                      <span className="block text-xs text-gray-500">Автоматическое открывание форточки</span>
                    </span>
                    <span className="text-gray-600">+{formatPrice(optionPrices.termoprivod)} ₽</span>
                  </label>
                  
                  {/* Фундамент */}
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      config.options.fundament
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Checkbox
                      checked={config.options.fundament}
                      onCheckedChange={() => handleOptionToggle('fundament')}
                    />
                    <span className="flex-1">
                      <span className="font-medium">Фундамент из бруса</span>
                      <span className="block text-xs text-gray-500">Для теплицы {config.width}x{config.length} м</span>
                    </span>
                    <span className="text-gray-600">+{formatPrice(optionPrices.fundament[config.length])} ₽</span>
                  </label>
                  
                  {/* Монтаж */}
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      config.options.montazh
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Checkbox
                      checked={config.options.montazh}
                      onCheckedChange={() => handleOptionToggle('montazh')}
                    />
                    <span className="flex-1">
                      <span className="font-medium">Профессиональный монтаж</span>
                      <span className="block text-xs text-gray-500">Для {typeNames[config.type].toLowerCase()} {config.width}x{config.length} м</span>
                    </span>
                    <span className="text-gray-600">+{formatPrice(optionPrices.montazh[config.type][config.length])} ₽</span>
                  </label>
                  
                </div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="lg:w-96">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Ваша конфигурация</h2>

              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Тип</span>
                  <span className="font-medium">{typeNames[config.type]}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Каркас</span>
                  <span className="font-medium">{frameNames[config.frame]}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Размер</span>
                  <span className="font-medium">{config.width}x{config.length} м</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Поликарбонат</span>
                  <span className="font-medium text-right">{polyNames[config.polycarbonate]}</span>
                </div>
                
                {(config.options.fortochka || config.options.termoprivod || config.options.fundament || config.options.montazh) && (
                  <div className="py-2 border-b">
                    <span className="text-gray-600 block mb-2">Опции:</span>
                    <ul className="space-y-1 text-sm">
                      {config.options.fortochka && (
                        <li className="flex justify-between">
                          <span>Доп. форточка</span>
                          <span>+{formatPrice(optionPrices.fortochka)} ₽</span>
                        </li>
                      )}
                      {config.options.termoprivod && (
                        <li className="flex justify-between">
                          <span>Термопривод</span>
                          <span>+{formatPrice(optionPrices.termoprivod)} ₽</span>
                        </li>
                      )}
                      {config.options.fundament && (
                        <li className="flex justify-between">
                          <span>Фундамент из бруса</span>
                          <span>+{formatPrice(optionPrices.fundament[config.length])} ₽</span>
                        </li>
                      )}
                      {config.options.montazh && (
                        <li className="flex justify-between">
                          <span>Монтаж</span>
                          <span>+{formatPrice(optionPrices.montazh[config.type][config.length])} ₽</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-lg font-bold">Итого</span>
                  <span className="text-3xl font-bold text-emerald-700">{formatPrice(totalPrice)} ₽</span>
                </div>

                <Button 
                  onClick={handleAddToCart}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 mb-4"
                >
                  Добавить в корзину
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-xs text-gray-500 text-center mb-4">
                  Точная стоимость будет рассчитана менеджером после оформления заказа
                </p>
                
                {/* Quick contact */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-3 text-center">Есть вопросы?</p>
                  <div className="flex gap-2">
                    <a href={`tel:${companyInfo.phoneClean}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Phone className="w-4 h-4" />
                      </Button>
                    </a>
                    <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" size="sm" className="w-full border-green-500 text-green-600">
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </a>
                    <a href={companyInfo.social.telegram} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" size="sm" className="w-full border-sky-500 text-sky-600">
                        <Send className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CalculatorPage;
